import { NSARouterMakerResult } from "namirasoft-account-react";
import { JournalEntryFullRow, JournalEntryMode, JournalEntryRequestReportRow, JournalEntryRequestReportRowRow } from "namirasoft-erp";
import { NSBox, NSBoxDate, NSBoxEnum, NSButtonBlue, NSButtonGreen, NSButtonRed, NSColumn, NSLine, NSLoading, NSPanel, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { JournalEntryLinesView } from "../components/JournalEntryLinesView";
import { ErpLayout } from "../ErpLayout";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpRoutes } from "../ErpRoutes";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";

interface JournalEntryRequestReportPageProps extends NSARouterMakerResult, ErpRouterProps { }

interface JournalEntryRequestReportPageState
{
    loading: boolean;
    report: JournalEntryRequestReportRow | null;
    scopeVersion: number;
}

const MODE_STYLE: { [mode: string]: { color: string; background: string; label: string } } = {
    [JournalEntryMode.RequestToAdd]: { color: "#1565c0", background: "#e3f2fd", label: "Request To Add" },
    [JournalEntryMode.RequestToEdit]: { color: "#ef6c00", background: "#fff3e0", label: "Request To Edit" },
    [JournalEntryMode.RequestToDelete]: { color: "#c62828", background: "#ffebee", label: "Request To Delete" },
};

const MODE_EFFECT: { [mode: string]: string } = {
    [JournalEntryMode.RequestToAdd]: "Approving posts this journal entry as a normal entry.",
    [JournalEntryMode.RequestToEdit]: "Approving replaces the header and the lines of the original journal entry with the requested ones.",
    [JournalEntryMode.RequestToDelete]: "Approving deletes the original journal entry.",
};

export default class JournalEntryRequestReportPage extends Component<JournalEntryRequestReportPageProps, JournalEntryRequestReportPageState>
{
    override state: JournalEntryRequestReportPageState = { loading: false, report: null, scopeVersion: 0 };

    private unsubscribeScope: (() => void) | null = null;

    FromDate = createRef<NSBoxDate>();
    ToDate = createRef<NSBoxDate>();
    Mode = createRef<NSBoxEnum<JournalEntryMode>>();

    override componentDidMount(): void
    {
        this.unsubscribeScope = ErpScope.subscribe(() =>
        {
            this.setState(s => ({ scopeVersion: s.scopeVersion + 1, report: null }));
        });
    }

    override componentWillUnmount(): void
    {
        this.unsubscribeScope?.();
    }

    private runReport = (onFinished: () => void): void =>
    {
        let companyId = ErpScope.getCompanyIdOrNull();
        let branchId = ErpScope.getBranchIdOrNull();
        let fiscalYearId = ErpScope.getFiscalYearIdOrNull();
        let from = this.FromDate.current?.getValue(false) ?? null;
        let to = this.ToDate.current?.getValue(false) ?? null;
        let modeRaw = this.Mode.current?.getValueOne(false) ?? null;
        let mode = Object.values(JournalEntryMode).includes(modeRaw as JournalEntryMode) ? (modeRaw as JournalEntryMode) : null;

        this.setState({ loading: true });
        this.props.servers.erp.report.JournalEntryRequest(
            companyId,
            branchId,
            fiscalYearId,
            from ? new Date(from) : null,
            to ? new Date(to) : null,
            mode,
        ).then((res: JournalEntryRequestReportRow) =>
        {
            this.setState({ loading: false, report: res });
            onFinished();
        }).catch(() =>
        {
            // The server already reported the error through the notifier.
            this.setState({ loading: false });
            onFinished();
        });
    };

    private removeRow(id: string): void
    {
        let report = this.state.report;
        if (!report)
            return;
        let rows = report.rows.filter(r => r.journal_entry.id !== id);
        this.setState({ report: { rows, count: rows.length } });
    }

    private approve = (id: string, onFinished: () => void): void =>
    {
        this.props.servers.erp.journal_entry.Approve(id)
            .then(() =>
            {
                this.removeRow(id);
                onFinished();
            })
            .catch(() =>
            {
                // The server already reported the error through the notifier.
                onFinished();
            });
    };

    private reject = (id: string, onFinished: () => void): void =>
    {
        this.props.servers.erp.journal_entry.Reject(id)
            .then(() =>
            {
                this.removeRow(id);
                onFinished();
            })
            .catch(() =>
            {
                // The server already reported the error through the notifier.
                onFinished();
            });
    };

    private entryCurrencyId(je: JournalEntryFullRow): string
    {
        let lines = je.lines ?? [];
        let firstCurrencyId = lines.find((r) => !!r.currency_id)?.currency_id ?? "";
        let isMulti = lines.some((r) => r.currency_id && r.currency_id !== firstCurrencyId);
        return isMulti ? "" : firstCurrencyId;
    }

    private formatMoney(value: number | string | null | undefined): string
    {
        let n = Number(value) || 0;
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    private formatDateTime(value: Date | string | null | undefined): string
    {
        if (!value)
            return "-";
        return new Date(value).toLocaleString();
    }

    private renderField(label: string, value: React.ReactNode): React.ReactNode
    {
        return (
            <div style={{ display: "flex", flexDirection: "column", minWidth: 140 }}>
                <span style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>{label}</span>
                <span style={{ fontSize: 14 }}>{value}</span>
            </div>
        );
    }

    private renderBadge(mode: JournalEntryMode): React.ReactNode
    {
        let style = MODE_STYLE[mode] ?? { color: "#444", background: "#eee", label: mode };
        return (
            <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: "bold", color: style.color, background: style.background }}>
                {style.label}
            </span>
        );
    }

    private renderHeader(je: JournalEntryFullRow, reference: JournalEntryFullRow | null): React.ReactNode
    {
        return (
            <NSColumn style={{ gap: 8, width: "100%" }}>
                <NSRow style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    {this.renderBadge(je.mode)}
                    <span style={{ fontWeight: "bold", color: "hsla(234, 64%, 22%, 1)" }}>{je.id}</span>
                    <span style={{ fontSize: 12, color: "#666" }}>{MODE_EFFECT[je.mode] ?? ""}</span>
                </NSRow>
                <NSRow style={{ gap: 24, flexWrap: "wrap" }}>
                    {this.renderField("Date Time", this.formatDateTime(je.date_time))}
                    {this.renderField("Reference Number", je.reference_number || "-")}
                    {this.renderField("Reference Date Time", this.formatDateTime(je.reference_date_time))}
                    {this.renderField("Enabled", je.enabled ? "Yes" : "No")}
                    {this.renderField("Total Debit", this.formatMoney(je.total_debit))}
                    {this.renderField("Total Credit", this.formatMoney(je.total_credit))}
                    {this.renderField("Requested At", this.formatDateTime(je.created_at))}
                    {reference && this.renderField("Original Journal Entry", reference.id)}
                </NSRow>
                {je.description && (
                    <div style={{ fontSize: 13, color: "#333", whiteSpace: "pre-wrap" }}>{je.description}</div>
                )}
            </NSColumn>
        );
    }

    private renderRow(row: JournalEntryRequestReportRowRow): React.ReactNode
    {
        let je = row.journal_entry;
        let reference = row.reference_journal_entry;
        let style = MODE_STYLE[je.mode];
        return (
            <NSPanel key={je.id} style={{ padding: 12, width: "100%", borderLeft: `4px solid ${style?.color ?? "#999"}` }}>
                <NSColumn style={{ gap: 8, width: "100%" }}>
                    {this.renderHeader(je, reference)}
                    <JournalEntryLinesView
                        {...this.props}
                        journal_entry_id={je.id}
                        currency_id={this.entryCurrencyId(je)}
                        rows={je.lines ?? []}
                        mode={je.mode}
                    />
                    {reference && (
                        <JournalEntryLinesView
                            {...this.props}
                            journal_entry_id={reference.id}
                            currency_id={this.entryCurrencyId(reference)}
                            rows={reference.lines ?? []}
                            mode={reference.mode}
                            title={`Original Journal Entry Lines (${reference.id})`}
                        />
                    )}
                    <NSRow style={{ gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 8 }}>
                        <NSButtonBlue
                            title="View"
                            onClick={{ action: (onFinished) => { this.props.url.navigate(ErpRoutes.JournalEntryView(je.id)); onFinished(); }, showLoading: false }}
                        />
                        {reference && (
                            <NSButtonBlue
                                title="View Original"
                                onClick={{ action: (onFinished) => { this.props.url.navigate(ErpRoutes.JournalEntryView(reference.id)); onFinished(); }, showLoading: false }}
                            />
                        )}
                        <NSButtonRed
                            title="Reject"
                            onClick={{ action: (onFinished) => this.reject(je.id, onFinished), showLoading: true }}
                        />
                        <NSButtonGreen
                            title="Approve"
                            onClick={{ action: (onFinished) => this.approve(je.id, onFinished), showLoading: true }}
                        />
                    </NSRow>
                </NSColumn>
            </NSPanel>
        );
    }

    private renderResults(): React.ReactNode
    {
        let { report } = this.state;
        if (!report)
            return null;
        if (report.rows.length === 0)
            return <div style={{ padding: 24, color: "#666" }}>No pending requests match the selected filters.</div>;
        return (
            <NSColumn style={{ gap: 16, width: "100%" }}>
                <div style={{ fontSize: 13, color: "#666" }}>{report.count} pending request{report.count === 1 ? "" : "s"}</div>
                {report.rows.map(row => this.renderRow(row))}
            </NSColumn>
        );
    }

    override render(): React.ReactNode
    {
        return (
            <ErpLayout {...this.props}
                mode={ErpScopeMode.Filter}
                fields={[ErpScopeField.Company, ErpScopeField.Branch, ErpScopeField.FiscalYear]}
                action={{ title: "Approval Requests", description: "Pending journal entry requests waiting for approval" }}
            >
                <NSColumn style={{ width: "100%", alignItems: "center" }}>
                    <NSRow style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center" }}>
                        <NSBoxDate
                            ref={this.FromDate}
                            title="From Date"
                            required={false}
                        />
                        <NSBoxDate
                            ref={this.ToDate}
                            title="To Date"
                            required={false}
                        />
                        <NSBoxEnum<JournalEntryMode>
                            ref={this.Mode}
                            title="Mode"
                            required={false}
                            multiple={false}
                            getEnumObject={() => ({
                                RequestToAdd: JournalEntryMode.RequestToAdd,
                                RequestToEdit: JournalEntryMode.RequestToEdit,
                                RequestToDelete: JournalEntryMode.RequestToDelete,
                            })}
                            placeholder="All request modes"
                            style={{ width: NSBox.width.one }}
                        />
                        <NSButtonBlue
                            title="Run Report"
                            onClick={{ action: this.runReport, showLoading: true }}
                        />
                    </NSRow>
                    <NSLine />
                    {this.state.loading && <NSLoading />}
                    {!this.state.loading && this.renderResults()}
                </NSColumn>
            </ErpLayout>
        );
    }
}